import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AppConfigService } from './appconfig.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly AUTH_ERROR_CODES = [
        'block_iframe_reload',
        'interaction_required',
        'login_required',
        'token_expired'
    ];

    constructor(
        private readonly msalService: MsalService | null,
        private readonly config: AppConfigService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Only attach token to our own API calls
        if (!request.url.startsWith(this.config.getConfig('apiUrl'))) {
            return next.handle(request);
        }

        // Fast path: use cached token from sessionStorage
        const cachedToken = this.getValidStoredToken();
        if (cachedToken) {
            return next.handle(this.cloneWithToken(request, cachedToken));
        }

        // If MSAL is not available or no active account, send without token
        const account = this.msalService?.instance?.getActiveAccount();
        if (!this.msalService?.instance || !account) {
            console.warn('[AuthInterceptor] MSAL or account not available, sending request without token');
            return next.handle(request);
        }

        // Acquire token silently, store it, then attach
        const scopes = [`${this.config.getConfig('clientId')}/.default`];

        return from(this.msalService.instance.acquireTokenSilent({ scopes, account })).pipe(
            switchMap((result) => {
                this.storeToken(result.accessToken, result.expiresOn);
                return next.handle(this.cloneWithToken(request, result.accessToken));
            }),
            catchError((error: any) => {
                console.error('[AuthInterceptor] Token acquisition failed:', error);

                if (this.isAuthenticationError(error)) {
                    this.reacquireTokenOrRelogin(scopes);
                }

                return throwError(() => error);
            })
        );
    }

    /** Read access_token from sessionStorage only if it hasn't expired */
    private getValidStoredToken(): string | null {
        const token = sessionStorage.getItem('access_token');
        const expiresOn = sessionStorage.getItem('access_token_expires_on');

        if (!token || (expiresOn && new Date(expiresOn).getTime() <= Date.now())) {
            this.clearStoredTokens();
            return null;
        }

        return token;
    }

    /** Clone request with Authorization header */
    private cloneWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
        });
    }

    /** Store token and expiry in sessionStorage */
    private storeToken(accessToken: string, expiresOn?: Date | null): void {
        sessionStorage.setItem('access_token', accessToken);
        if (expiresOn) {
            sessionStorage.setItem('access_token_expires_on', expiresOn.toISOString());
        }
    }

    /** Clear stored tokens */
    private clearStoredTokens(): void {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token_expires_on');
    }

    /** Check if the error is an authentication/token error */
    private isAuthenticationError(error: any): boolean {
        if (this.AUTH_ERROR_CODES.includes(error?.errorCode) ||
            error?.name === 'BrowserAuthError' ||
            error?.name === 'InteractionRequiredAuthError') {
            return true;
        }

        const errorMessage = error?.message?.toLowerCase() || '';
        return errorMessage.includes('token') &&
            (errorMessage.includes('expired') || errorMessage.includes('invalid'));
    }

    /** Try to acquire token via MSAL first, if fails then clear storage and redirect to login */
    private reacquireTokenOrRelogin(scopes: string[]): void {
        const account = this.msalService?.instance?.getActiveAccount();

        if (account) {
            // Try to acquire a fresh token before clearing
            this.msalService!.instance.acquireTokenSilent({ scopes, account, forceRefresh: true })
                .then((result) => {
                    this.storeToken(result.accessToken, result.expiresOn);
                })
                .catch(() => {
                    this.clearAllAndRelogin(scopes);
                });
        } else {
            this.clearAllAndRelogin(scopes);
        }
    }

    /** Clear all auth data and trigger MSAL login redirect */
    private clearAllAndRelogin(scopes: string[]): void {
        this.clearStoredTokens();
        sessionStorage.removeItem('group_membership_verified');
        sessionStorage.removeItem('group_membership_group_id');

        try {
            this.msalService?.loginRedirect({
                scopes,
                redirectStartPage: window.location.pathname,
            });
        } catch (err) {
            console.error('[AuthInterceptor] Failed to trigger MSAL login redirect:', err);
        }
    }
}
